"use client";
import { TRANSITION_EASE_OUT } from "@/config/transitions";
import { cn } from "@/lib/utils";
import RichText from "@app/_blocks/RichText";
import { useCartState, useMarket } from "@app/_context";
import useTranslate from "@app/_hooks/useTranslate";
import { InfoIcon } from "@app/_icons/InfoIcon";
import { useCheckoutContext } from "@app/_pages/checkout-v2/Checkout.hooks";
import { Dialog, Input, Loader } from "@components/ngg";
import { m } from "framer-motion";
import { XIcon } from "lucide-react";
import { useState, type ComponentProps } from "react";
import LoadingIconNew from "../icons/LoadingIconNew";
import useEurobonus from "./useEurobonus";

type Props = ComponentProps<"form"> & {};

export default function EurobonusForm({ className, ...props }: Props) {
  const t = useTranslate();

  const { loading } = useCartState();
  const market = useMarket().state.market;
  const [dialog, setDialog] = useState(false);
  const { eurobonusBlock } = useCheckoutContext();
  const {
    inputRef,
    eurobonusNumber,
    handleSubmit,
    handleChange,
    isValidEurobonusNumber,
  } = useEurobonus();

  return (
    <>
      <form
        onSubmit={(event) => handleSubmit(event)}
        className={cn(
          "mt-2 grid w-full grid-cols-[1fr_auto] grid-rows-[auto_auto] items-center gap-x-2",
          className,
        )}
        {...props}
      >
        <Input
          fullWidth
          classNames={{
            wrapper: "grid-rows-subgrid grid row-span-2 peer",
            input: "p-2 border-none placeholder:text-base",
            inputWrapper: "focus-within:border-black border flex items-center",
            hint: "text-gray-300",
          }}
          autoFocus
          ref={inputRef}
          type="text"
          required
          name="eurobonusNumber"
          onChange={handleChange}
          placeholder={t("enterYourEurobonusNumber") ?? "Ange Eurobonusnummer"}
          value={eurobonusNumber}
          readOnly={isValidEurobonusNumber}
          hint={t("eurobonusWarningMessage")}
          endAdornment={
            eurobonusBlock?.content && (
              <button type="button" className="mr-2 inline-block">
                <InfoIcon className="size-4" onClick={() => setDialog(true)} />
              </button>
            )
          }
        />
        <button
          disabled={!eurobonusNumber}
          type="submit"
          className="relative h-full min-w-24 bg-black px-4 text-center text-base tracking-wide text-white disabled:opacity-30"
        >
          {isValidEurobonusNumber ? t("remove") : t("apply")}
          {loading.updateCartMetadata && (
            <m.div
              transition={TRANSITION_EASE_OUT}
              initial={{
                opacity: 0,
                backgroundColor: "rgba(0, 0, 0, 0)",
                backdropFilter: "blur(0px)",
              }}
              animate={{
                opacity: 1,
                backgroundColor: "rgba(0, 0, 0, 0.3)",
                backdropFilter: "blur(2px)",
              }}
              exit={{
                opacity: 0,
                backgroundColor: "rgba(0, 0, 0, 0)",
                backdropFilter: "blur(0px)",
              }}
              className={cn(
                "pointer-events-none absolute inset-0 flex h-full w-full items-center justify-center",
              )}
            >
              <Loader
                LoaderComponent={<LoadingIconNew className="text-white/70" />}
              />
            </m.div>
          )}
        </button>
      </form>
      {eurobonusBlock?.content && (
        <Dialog
          classNames={{
            panel: "text-left p-8 max-w-3xl overflow-auto max-h-screen-2/3",
            panelHeader: "px-0",
            title: "text-lg lg:text-xl ma mb-4  ",
          }}
          open={dialog}
          onClose={() => setDialog(false)}
          title={eurobonusBlock?.title}
        >
          <button
            className="absolute top-0 right-0 p-4"
            onClick={() => setDialog(false)}
          >
            <XIcon strokeWidth={1} className="size-4" />
          </button>
          <RichText
            className="prose-td:not-prose prose-a:underline prose-ul:list-disc prose-ul:py-4 prose-ul:pl-4 prose-li:my-2 prose-table:my-2 prose-table:border-collapse prose-td:border prose-td:border-black prose-td:p-2 max-w-none"
            content={eurobonusBlock?.content}
            market={market.code}
            locale={market.locale}
          />
        </Dialog>
      )}
    </>
  );
}
